import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Loader2, RefreshCw, ChevronDown, ChevronUp, List } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { ResponseListModal } from './ResponseListModal';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

interface ResponseSummaryProps {
  surveyId: string;
  questionId: string;
  questionText: string;
  responseCount: number;
  className?: string;
}

interface SummaryData {
  id: string;
  summary_text: string;
  response_count: number;
  last_updated_at: string;
  status: 'generating' | 'complete' | 'error';
  error_message?: string;
}

export function ResponseSummary({ 
  surveyId, 
  questionId, 
  questionText,
  responseCount,
  className 
}: ResponseSummaryProps) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showResponses, setShowResponses] = useState(false);

  useEffect(() => {
    if (expanded && !summary && !loading) {
      loadSummary();
    }
  }, [expanded]);

  const loadSummary = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data: existingSummary, error: fetchError } = await supabase
        .from('survey_response_summaries')
        .select('*')
        .eq('survey_id', surveyId)
        .eq('question_id', questionId)
        .maybeSingle();

      if (fetchError) {
        throw new Error(`Failed to fetch summary: ${fetchError.message}`, { cause: fetchError });
      }

      if (existingSummary) {
        setSummary(existingSummary);
        // Check if summary is stale (older than 24 hours)
        const lastUpdated = new Date(existingSummary.last_updated_at);
        const isStale = Date.now() - lastUpdated.getTime() > 24 * 60 * 60 * 1000;
        
        if (isStale && existingSummary.status !== 'generating') {
          await generateSummary();
        }
      } else if (responseCount > 0) {
        await generateSummary();
      }
    } catch (err) {
      console.error('Error loading summary:', err);
      setError(err instanceof Error ? err : new Error('Failed to load summary'));
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    if (!user) return;

    try {
      setGenerating(true);
      setError(null);

      // Call RPC function to generate summary
      const { data: summaryData, error: rpcError } = await supabase
        .rpc('generate_summary_text', { p_question_id: questionId });

      if (rpcError) {
        throw new Error(`Failed to generate summary: ${rpcError.message}`, { cause: rpcError });
      }

      // Create or update summary record
      const { data: summaryRecord, error: upsertError } = await supabase
        .from('survey_response_summaries')
        .upsert({
          survey_id: surveyId,
          question_id: questionId,
          status: 'complete',
          response_count: responseCount,
          summary_text: summaryData,
          last_updated_at: new Date().toISOString(),
          generated_by_user_id: user.id
        }, {
          onConflict: 'survey_id,question_id',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (upsertError) {
        throw new Error(`Failed to save summary: ${upsertError.message}`, { cause: upsertError });
      }

      setSummary(summaryRecord);

    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err instanceof Error ? err : new Error('Failed to generate summary'));

      // Update summary status to error if it exists
      if (summary?.id) {
        const { error: updateError } = await supabase
          .from('survey_response_summaries')
          .update({
            status: 'error',
            error_message: err instanceof Error ? err.message : 'Unknown error',
            last_updated_at: new Date().toISOString(),
            generated_by_user_id: user.id
          })
          .eq('id', summary.id);

        if (updateError) {
          console.error('Error updating summary status:', updateError);
        }
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRefresh = async () => {
    // Check if last update was within 1 hour
    if (summary?.last_updated_at) {
      const lastUpdated = new Date(summary.last_updated_at);
      const timeSinceUpdate = Date.now() - lastUpdated.getTime();
      
      if (timeSinceUpdate < 60 * 60 * 1000) {
        if (!confirm('This summary was updated less than an hour ago. Do you want to regenerate it anyway?')) {
          return;
        }
      }
    }

    await generateSummary();
  };

  if (!user) return null;

  return (
    <div className={cn("bg-gray-50 dark:bg-gray-700/50 rounded-lg", className)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Response Summary
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {responseCount} responses
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {error && (
            <ErrorDisplay 
              error={error}
              context="Response Summary"
              onRetry={loadSummary}
              className="mb-4"
            />
          )}

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : summary ? (
            <div className="space-y-4">
              {summary.status === 'generating' ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Generating the summary...
                  </span>
                </div>
              ) : (
                <>
                  <div className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                    {summary.summary_text}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      Last updated: {new Date(summary.last_updated_at).toLocaleString()}
                    </span>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowResponses(true)}
                      >
                        <List className="h-4 w-4 mr-2" />
                        View All Responses
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={generating}
                      >
                        {generating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : responseCount > 0 ? (
            <div className="flex items-center justify-center py-4">
              <Button onClick={generateSummary} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating Summary...
                  </>
                ) : (
                  'Generate Summary'
                )}
              </Button>
            </div>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No responses to summarize yet
            </div>
          )}
        </div>
      )}

      <ResponseListModal
        isOpen={showResponses}
        onClose={() => setShowResponses(false)}
        surveyId={surveyId}
        questionId={questionId}
        questionText={questionText}
      />
    </div>
  );
}